import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
  Mutation,
  MutationAddRestApiKeyArgs,
  MutationDeleteRestApiKeyArgs,
  MutationUpdateRestApiKeyArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ADD_REST_API_KEY, DELETE_REST_API_KEY, UPDATE_REST_API_KEY } from 'graphql/mutations';
import { GET_REST_API_KEYS } from 'graphql/queries';
import { useEffect } from 'react';
import { deleteRestApiKeyAction, setRestApiKeysAction, updateRestApiKeyAction } from 'state/actions/restApiKeys';
import { restApiKeysVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

const useRestApiKey = ({ handleFetchError }: BaseHookProps) => {
  const restApiKeys = useReactiveVar(restApiKeysVar);

  const [getRestApiKeysQuery, { loading }] = useLazyQuery<Pick<Query, 'getRestApiKeys'>>(GET_REST_API_KEYS, {
    onCompleted: (data) => {
      setRestApiKeysAction(data.getRestApiKeys);
    },
    onError: () => {
      handleFetchError('Error while fetching API keys');
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getRestApiKeysQuery();
  }, [getRestApiKeysQuery]);

  const [addRestApiKeyMutation, { loading: addRestApiKeyLoading }] = useMutation<
    Pick<Mutation, 'addRestApiKey'>,
    MutationAddRestApiKeyArgs
  >(ADD_REST_API_KEY, {
    onCompleted: (data) => data,
    onError: () => {
      handleFetchError('Error while adding rest API key');
    }
  });

  const addRestApiKey = async (description: string) => {
    const { data } = await addRestApiKeyMutation({
      variables: { description }
    });
    if (data) {
      getRestApiKeysQuery();
    }
    return data;
  };

  const [updateRestApiKeyMutation, { loading: updateRestApiKeyLoading }] = useMutation<
    Pick<Mutation, 'updateRestApiKey'>,
    MutationUpdateRestApiKeyArgs
  >(UPDATE_REST_API_KEY, {
    onCompleted: (data) => data,
    onError: () => handleFetchError('Error while updating rest API key')
  });

  const updateRestApiKey = async (id: string, description: string) => {
    const { data } = await updateRestApiKeyMutation({
      variables: { id, description }
    });
    if (data) {
      updateRestApiKeyAction(data.updateRestApiKey);
    }
    return data;
  };

  const [deleteRestApiKeyMutation, { loading: deleteRestApiKeyLoading }] = useMutation<
    Pick<Mutation, 'deleteRestApiKey'>,
    MutationDeleteRestApiKeyArgs
  >(DELETE_REST_API_KEY, {
    onCompleted: (data) => data,
    onError: () => handleFetchError('Error while deleting rest API key')
  });

  const deleteRestApiKey = async (id: string) => {
    const { data } = await deleteRestApiKeyMutation({
      variables: { id }
    });
    if (data) {
      deleteRestApiKeyAction(data.deleteRestApiKey);
    }
    return data;
  };

  return {
    restApiKeys,
    loading,
    addRestApiKey,
    addRestApiKeyLoading,
    updateRestApiKey,
    updateRestApiKeyLoading,
    deleteRestApiKey,
    deleteRestApiKeyLoading
  };
};

export default useRestApiKey;
